/* === GLOBALS === */
let resizeTimeout;

const pageLifecycle = new AbortController();
const { signal } = pageLifecycle;
const sessionId = crypto.randomUUID();
const ONEGATEWAY_URL = globalThis.location.origin.replace("curation", "onegateway");
const LOGS_ENDPOINT = "/logs/events";
const EVENTS = {
	ROUTING: "ui.view_page",
	ERROR: "ui.error",
	VALIDATION: "ui.validation",
	CLICK: "ui.click",
	DEVICE: "ui.device"
};

/* === ERROR HANDLING === */

const originalConsoleError = console.error;

console.error = (...args) => {
	originalConsoleError.apply(console, args);
	const errorData = args.find(arg => arg instanceof Error) || args[0];
	handleAngularError(errorData);
};

globalThis.addEventListener("unhandledrejection", ({ reason }) => handleAngularError(reason));

/* === UTILS === */

function handleAngularError(error) {
	if (!error || !(error instanceof Error)) return;

	const { message, stack } = error;
	const componentMethod = extractComponentMethodFromError(stack.substring(0, 200));
	const [className, methodName] = componentMethod.split(".");

	submitAnalyticsData(EVENTS.ERROR, {
		message,
		className,
		...(methodName && { methodName })
	});
}

function extractComponentMethodFromError(errorMessage) {
	const regex = /_([^ ]+)/;
	const match = errorMessage.match(regex);
	return match ? match[1] : "";
}

function onPageHide() {
	pageLifecycle.abort();
	clearTimeout(resizeTimeout);
}

function extractPathnameFromURL(url) {
	try {
		return new URL(url).pathname;
	} catch {
		return url;
	}
}

function cleanHTML(html) {
	if (!html) return "";
	return html
		.replace(/<!--container-->/, "")
		.replace(/<!--ng-container-->/, "")
		.substring(0, 200);
}

function submitDeviceAnalytics(event) {
	if (!event) return;

	const { innerWidth: currentWidth, innerHeight: currentHeight } = event;

	submitAnalyticsData(EVENTS.DEVICE, {
		width: currentWidth ?? globalThis.screen.width,
		height: currentHeight ?? globalThis.screen.height,
		userAgent: globalThis.navigator.userAgent
	});
}

function submitAnalyticsData(eventName, properties) {
	const isEventNameInvalid = !Object.values(EVENTS).includes(eventName);
	const JWT = localStorage.getItem("access_token");

	if (!JWT || isEventNameInvalid) return;

	const analytics = {
		sessionId,
		eventName,
		pageUrl: globalThis.location.pathname,
		properties
	};

	fetch(ONEGATEWAY_URL + LOGS_ENDPOINT, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${JWT}`
		},
		body: JSON.stringify(analytics)
	}).catch(() => {});
}

/* === EVENT HANDLERS === */

function onDocumentLoad(event) {
	document.body.addEventListener("click", onClick, { signal });
	globalThis.addEventListener("resize", onResize, { signal });
	globalThis.navigation.addEventListener("navigate", onRouteChange, { signal });
	globalThis.addEventListener("pagehide", onPageHide, { signal });
	globalThis.addEventListener("validationerror", onValidationError, { signal });

	submitDeviceAnalytics(event);
}

function onResize(event) {
	clearTimeout(resizeTimeout);
	resizeTimeout = setTimeout(submitDeviceAnalytics, 1000, event);
}

function onRouteChange(event) {
	const navigationFrom = globalThis.location.pathname;
	const navigationTo = extractPathnameFromURL(event.destination.url);

	submitAnalyticsData(EVENTS.ROUTING, { from: navigationFrom, to: navigationTo });
}

function onValidationError(event) {
	const { formName, invalidFields } = event.detail;
	submitAnalyticsData(EVENTS.VALIDATION, { formName, invalidFields });
}

function onClick(event) {
	if (!event?.target) return;

	const { target, pointerId, pointerType, clientX, clientY } = event;

	const { innerHTML: targetHTML, tagName: targetTagName, textContent: targetTextContent } = target;
	const {
		innerHTML: parentHTML,
		tagName: parentTagName,
		textContent: parentTextContent
	} = target.parentElement;

	submitAnalyticsData(EVENTS.CLICK, {
		pointerId,
		...(pointerType && { pointerType }),
		...(pointerId === 1 && {
			clientX,
			clientY
		}),
		targetElement: {
			html: cleanHTML(targetHTML),
			tagName: targetTagName,
			textContent: targetTextContent.trim()
		},
		parentElement: {
			html: cleanHTML(parentHTML),
			tagName: parentTagName,
			textContent: parentTextContent.trim()
		}
	});
}

/* === LISTENERS & OBSERVERS === */

document.addEventListener("DOMContentLoaded", onDocumentLoad);
